/**
 * DescriptionRulesView — /catalog-entry/rules  카탈로그 규칙(카탈로그 › 카탈로그 규칙 › 규칙)
 *
 * 약식 디스크립션 규칙 관리. 왼쪽 카테고리 셀렉트 + 규칙 목록(기본 ★), 오른쪽 편집기(자리 한 줄씩 표 — ⓑ) +
 * 미리보기(편집 300ms 뒤 자동 — ⓒ) + 버전 목록 + 고칠 목록(필수 빔, 2026-10-26). 자리 순서는 손잡이(⠿) 드래그 또는 ↑↓(현민 2026-10-23). 고치기 · 지우기 · 기본 교체 · 되돌리기는 영향 수 확인창을 거친다
 * ("카탈로그 행 N · 제품 M 이 다시 써집니다"). hcltech-parts V장 "디스크립션 규칙 화면". 현민 2026-10-23.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import { SPEC_CATEGORIES } from '@hcltech/shared/server-config/spec-category';
import * as api from '../api/client';
import type {
  DescriptionPreviewLine, DescriptionRuleInput, DescriptionRuleMissingRow, DescriptionRuleUsage, DescriptionRuleVersionView, DescriptionRuleView,
  ERuleTail, PatternItem, RewriteCount, TokenFormat, TokenGroup,
} from '../api/client';
import { E_DESC_TOKEN_ROLES, E_RULE_TAILS, RULE_TAIL_WORD, TOKEN_ROLE_WORD, catalogTableWord } from '../api/client';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { assert } from '../lib/assert';
import { escapeHtml } from '../lib/cells';

/** 편집 중인 규칙 한 벌 — 글만 쥔다. rule_id null = 새 규칙 */
interface RuleForm {
  rule_id: string | null;
  spec_category: string;
  name: string;
  pattern: PatternItem[];
  tail: ERuleTail;
}
/** 자리 주소 — 자리 하나가 pattern 어디 있나. inner null = 맨 위 자리, 숫자 = 그 묶음(item) 안 몇째 */
type FormatAddress = { item: number; inner: number | null };
/** 영향 수를 합치는 갈래 — 고치기 · 되돌리기 = 고른 행 + 기본이면 기본 행 · 지우기 = 고른 행 · 기본 교체 = 기본 행 */
type RuleAction = 'update' | 'delete' | 'set_default' | 'restore';

interface DescriptionRulesData {
  category: string;                        // 왼쪽 셀렉트 — SPEC_CATEGORIES 21
  rules: DescriptionRuleView[];            // 그 카테고리의 규칙(서버 정렬 — 기본 먼저)
  form: RuleForm | null;                   // null = 고른 규칙 없음
  preview: DescriptionPreviewLine[];       // 폼으로 렌더한 원문 PREVIEW_LIMIT 줄
  versions: DescriptionRuleVersionView[];  // 고른 규칙의 지난 버전(새 규칙은 [])
  missing: DescriptionRuleMissingRow[];    // 고칠 목록 — 지금 규칙으로 필수가 비는 행(새 규칙은 [])
  loading: boolean;
  saving: boolean;
  error: string;
}

const TAB_HREF = '/catalog-entry/rules';
const PREVIEW_DEBOUNCE_MS = 300;
const PREVIEW_LIMIT = 20;
const MISSING_ROWS_LIMIT = 500;   // 서버가 자르는 줄 수 — 같으면 "앞 500줄" 을 붙인다
const FIRST_CATEGORY: string = SPEC_CATEGORIES[0];
const NEW_FORMAT: TokenFormat = { kind: 'token', role: 'model', prefix: '', suffix: '', required: false };
const NEW_GROUP_PREFIX = '(';
const NEW_GROUP_SUFFIX = ')';
const ACTION_WORD: Record<RuleAction, string> = { update: '저장', delete: '지우기', set_default: '기본으로', restore: '이 버전으로 되돌리기' };

export class DescriptionRulesView extends SolaView<DescriptionRulesData> {
  override events = {
    click:  {
      '@rule': 'handleSelectRule', '@new-rule': 'handleNewRule', '@save': 'handleSave', '@delete': 'handleDelete',
      '@set-default': 'handleSetDefault', '@restore': 'handleRestore',
      '@format-up': 'handleFormatUp', '@format-down': 'handleFormatDown', '@format-remove': 'handleFormatRemove', '@format-add': 'handleFormatAdd',
      '@group-add': 'handleGroupAdd', '@group-unwrap': 'handleGroupUnwrap', '@group-format-add': 'handleGroupFormatAdd',
    },
    change: { '@category': 'handleCategory', '@format-role': 'handleFormatRole', '@format-required': 'handleFormatRequired', '@tail': 'handleTail' },
    input:  {
      '@name': 'handleName', '@format-prefix': 'handleFormatPrefix', '@format-suffix': 'handleFormatSuffix',
      '@group-prefix': 'handleGroupPrefix', '@group-suffix': 'handleGroupSuffix',
    },
  };

  private previewTimer: ReturnType<typeof setTimeout> | null = null;
  private draggingIndex: number | null = null;   // 끌고 있는 자리 줄 — dragstart ~ dragend

  constructor() {
    super();
    this.data = { category: FIRST_CATEGORY, rules: [], form: null, preview: [], versions: [], missing: [], loading: true, saving: false, error: '' };
  }

  override getTitle(): string {
    return '카탈로그 규칙 — HCLTech';
  }

  override onMount(): void {
    void this.loadRules(null);
  }

  render(): HTMLElement {
    const place = findMenuPlace(TAB_HREF);
    assert(place !== null, `카탈로그 규칙 탭이 메뉴에 없다: ${TAB_HREF} — MENU_GROUPS 카탈로그 › 카탈로그 규칙의 탭에 있어야 한다`);
    return htmlSolaWithInstance(this)`
      <div class="page page-description-rules pv-page">
        <header class="page-header">
          <h1>${menuHeading(place)}</h1>
          ${renderMenuTabs(place)}
        </header>
        <section class="page-body pv-body">${this.renderBody()}</section>
      </div>`;
  }

  // ─── 불러오기 ───
  /** 지금 카테고리의 규칙 목록. selectIdOrNull 이 목록에 있으면 그 규칙, 아니면 첫 규칙(기본)을 폼에 */
  private async loadRules(selectIdOrNull: string | null): Promise<void> {
    this.data = { ...this.data, loading: true, error: '' };
    this.refreshBody();
    try {
      const rules = await api.fetchDescriptionRules(this.data.category);
      const found = rules.find((rule) => rule.id === selectIdOrNull);
      const first: DescriptionRuleView | null = rules.length === 0 ? null : rules[0];
      const selected = found === undefined ? first : found;
      this.data = { ...this.data, rules, loading: false, form: selected === null ? null : formOfRule(selected), versions: [], missing: [], preview: [] };
      if (selected !== null) await this.loadVersionsAndPreview(selected.id);
    } catch (e) {
      this.data = { ...this.data, loading: false, error: messageOf(e) };
    }
    this.refreshBody();
  }

  /** 고른 규칙의 버전 목록 + 고칠 목록 + 지금 폼의 미리보기 */
  private async loadVersionsAndPreview(ruleId: string): Promise<void> {
    const [versions, missing] = await Promise.all([api.fetchDescriptionRuleVersions(ruleId), api.fetchMissingRequiredRows(ruleId), this.loadPreview()]);
    this.data = { ...this.data, versions, missing };
  }

  /** 지금 폼으로 POST /preview — DB 는 안 바뀐다. 자리 없는 폼은 빈 미리보기 */
  private async loadPreview(): Promise<void> {
    const form = this.data.form;
    if (form === null || form.pattern.length === 0) {
      this.data = { ...this.data, preview: [] };
      return;
    }
    try {
      const preview = await api.previewDescriptionRule(inputOfForm(form), PREVIEW_LIMIT);
      this.data = { ...this.data, preview };
    } catch (e) {
      this.data = { ...this.data, error: messageOf(e) };
    }
  }

  /** 편집 300ms 뒤 미리보기 — 치는 동안은 안 보낸다 */
  private schedulePreview(): void {
    if (this.previewTimer !== null) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => {
      this.previewTimer = null;
      void this.loadPreview().then(() => this.refreshRegion('.dr-preview', () => this.renderPreview()));
    }, PREVIEW_DEBOUNCE_MS);
  }

  // ─── 저장 · 지우기 · 기본 · 되돌리기 ───
  /**
   * 영향 수를 받아 확인창. 새 규칙(rule_id null)은 묻지 않는다.
   * missingInputOrNull 이 있으면 그 입력으로 다시 쓸 때 필수가 비는 행도 센다 — 고치기 · 되돌리기만(서버가 그 규칙의 지금 범위를 새 입력으로 읽는다. 지우기 · 기본 교체는 다른 규칙으로 다시 써서 null)
   */
  private async confirmWithUsage(action: RuleAction, ruleId: string, missingInputOrNull: DescriptionRuleInput | null): Promise<boolean> {
    const usage = await api.fetchDescriptionRuleUsage(ruleId);
    const count = rewriteCountOf(usage, action);
    const missing = missingInputOrNull === null ? null : await api.countMissingRequiredRows(ruleId, missingInputOrNull);
    return confirm(`${ACTION_WORD[action]} — ${rewriteWordOf(count)}\n이 규칙을 쓰는 행의 약식 디스크립션이 즉시 다시 써집니다. 견적 줄은 안 바뀝니다.${missingWordOf(missing)}`);
  }

  /** 한 액션을 돌리고 목록을 다시 받는다. 실패면 error 글 */
  private async run(action: () => Promise<string | null>): Promise<void> {
    this.data = { ...this.data, saving: true, error: '' };
    this.refreshBody();
    try {
      const selectIdOrNull = await action();
      this.data = { ...this.data, saving: false };
      await this.loadRules(selectIdOrNull);
    } catch (e) {
      this.data = { ...this.data, saving: false, error: messageOf(e) };
      this.refreshBody();
    }
  }

  // ─── 그리기 ───
  private refreshBody(): void {
    this.refreshRegion('.pv-body', () => this.renderBody());
    this.wireFormatDrag();
  }

  /** 자리 줄 드래그 — 줄의 빈 곳(입력 · 셀렉트 · 버튼 밖)을 잡아 다른 줄에 놓으면 그 자리에 끼운다.
   *  draggable 을 줄에 늘 켜 두면 안의 입력에서 글을 못 고르므로, mousedown 때 잡은 곳을 보고 켠다.
   *  Sola 의 events 는 drag 이벤트를 안 받아 그릴 때마다 직접 건다(ServerConfigEditor 꼴) */
  private wireFormatDrag(): void {
    const root = this._rootElement;
    if (root === undefined) return;   // Sola 가 optional 로 둔 자리 — 마운트 전엔 없다
    root.querySelectorAll<HTMLElement>('.dr-format-row').forEach((row) => {
      const index = indexOf(row);
      row.addEventListener('mousedown', (e) => {
        row.draggable = !isEditableTarget(e.target);
      });
      row.addEventListener('dragstart', (e) => {
        this.draggingIndex = index;
        row.classList.add('is-dragging');
        const transfer = (e as DragEvent).dataTransfer;
        if (transfer !== null) transfer.setData('text/plain', String(index));
      });
      row.addEventListener('dragend', () => {
        this.draggingIndex = null;
        row.draggable = false;
        root.querySelectorAll('.dr-format-row').forEach((r) => r.classList.remove('is-dragging', 'is-drop-target'));
      });
      row.addEventListener('dragover', (e) => {
        if (this.draggingIndex === null || this.draggingIndex === index) return;
        e.preventDefault();
        row.classList.add('is-drop-target');
      });
      row.addEventListener('dragleave', () => row.classList.remove('is-drop-target'));
      row.addEventListener('drop', (e) => {
        e.preventDefault();
        if (this.draggingIndex === null || this.draggingIndex === index) return;
        this.replacePattern(droppedFormat(this.form().pattern, this.draggingIndex, index));
      });
    });
  }

  private renderBody(): string {
    const { form, loading, error } = this.data;
    const errorLine = error === '' ? '' : `<p class="dr-error">${escapeHtml(error)}</p>`;
    const right = form === null
      ? `<p class="text-muted">${loading ? '불러오는 중…' : '규칙이 없습니다 — [새 규칙]으로 만듭니다'}</p>`
      : `${this.renderEditor(form)}<div class="dr-preview">${this.renderPreview()}</div>${this.renderVersions()}${this.renderMissing()}`;
    return `${errorLine}
      <div class="dr-layout">
        <aside class="dr-side">${this.renderRuleList()}</aside>
        <section class="dr-main">${right}</section>
      </div>`;
  }

  /** 왼쪽 — 카테고리 셀렉트 · 규칙 목록(기본 ★) · [새 규칙] */
  private renderRuleList(): string {
    const { category, rules, form } = this.data;
    const options = SPEC_CATEGORIES.map((c) => `<option value="${c}" ${c === category ? 'selected' : ''}>${c}</option>`).join('');
    const selectedId = form === null ? null : form.rule_id;
    const items = rules.map((rule) => `
      <li><button type="button" class="dr-rule ${rule.id === selectedId ? 'is-selected' : ''}" data-action="rule" data-id="${rule.id}">
        ${rule.is_default ? '<span class="dr-star" title="기본 규칙">★</span>' : '<span class="dr-star"></span>'}
        <span class="dr-rule-name">${escapeHtml(rule.name)}</span><span class="dr-rule-ver text-muted">버전 ${rule.version}</span>
      </button></li>`).join('');
    const newItem = selectedId === null && form !== null ? '<li class="dr-rule is-selected dr-rule-new">새 규칙(저장 전)</li>' : '';
    return `
      <select class="pc-select dr-category" data-action="category">${options}</select>
      <ul class="dr-rule-list">${items}${newItem}</ul>
      <button type="button" class="btn-ghost" data-action="new-rule">+ 새 규칙</button>`;
  }

  /** 오른쪽 위 — 이름 · 자리 표 · 꼬리 · 버튼 */
  private renderEditor(form: RuleForm): string {
    const { saving } = this.data;
    const bNew = form.rule_id === null;
    const rows = form.pattern.map((item, index) => (item.kind === 'group'
      ? this.renderGroupRow(item, index, form.pattern.length)
      : this.renderFormatRow(item, { item: index, inner: null }, form.pattern.length))).join('');
    const tails = E_RULE_TAILS.map((tail) => `
      <label class="pc-check"><input type="radio" name="tail" data-action="tail" value="${tail}" ${tail === form.tail ? 'checked' : ''}> ${RULE_TAIL_WORD[tail]}</label>`).join('');
    const saved = this.data.rules.find((rule) => rule.id === form.rule_id);
    const isDefault = saved !== undefined && saved.is_default;
    const bDirty = saved === undefined || !isSameRule(saved, form);
    const dirtyWord = bDirty ? '<span class="dr-dirty">저장 전 — 바뀐 것은 서버에 안 갔습니다</span>' : '<span class="text-muted dr-dirty">저장됨</span>';
    const disabled = saving ? 'disabled' : '';
    return `
      <div class="dr-editor">
        <label class="dr-field"><span>이름</span><input type="text" class="pd-input" data-action="name" value="${escapeHtml(form.name)}" placeholder="규칙 이름"></label>
        <table class="pv-table dr-format-table">
          <thead><tr><th>순서</th><th>역할</th><th>앞말</th><th>뒷말</th><th>필수</th><th></th></tr></thead>
          <tbody>${rows.length === 0 ? '<tr><td colspan="6" class="text-muted">자리가 없습니다 — [자리 추가]</td></tr>' : rows}</tbody>
        </table>
        <button type="button" class="btn-ghost" data-action="format-add" ${disabled}>+ 자리 추가</button>
        <button type="button" class="btn-ghost" data-action="group-add" ${disabled}>+ 묶음 추가</button>
        <div class="dr-tail">${tails}</div>
        <div class="dr-actions">
          <button type="button" class="btn-primary" data-action="save" ${disabled}>${bNew ? '만들기' : '저장'}</button>
          ${bNew ? '' : `<button type="button" class="btn-ghost" data-action="set-default" ${disabled || isDefault ? 'disabled' : ''}>기본으로</button>`}
          ${bNew ? '' : `<button type="button" class="btn-ghost dr-danger" data-action="delete" ${disabled || isDefault ? 'disabled' : ''} title="${isDefault ? '기본 규칙은 못 지웁니다 — 다른 규칙을 기본으로 한 뒤' : ''}">지우기</button>`}
          ${dirtyWord}
        </div>
      </div>`;
  }

  /** 묶음 줄 — 머리 줄(끌기 대상) + 들여 쓴 안 자리 줄 + [+ 안에 자리 추가] 줄 */
  private renderGroupRow(group: TokenGroup, index: number, count: number): string {
    const inners = group.formats.map((format, inner) => this.renderFormatRow(format, { item: index, inner }, group.formats.length)).join('');
    return `
      <tr class="dr-format-row dr-group-row" data-index="${index}">
        <td class="dr-order"><span class="dr-handle" title="줄의 빈 곳을 끌어 순서를 바꾼다">⠿</span> ${index + 1}
          <button type="button" class="dr-mini" data-action="format-up" data-index="${index}" ${index === 0 ? 'disabled' : ''} title="위로">↑</button>
          <button type="button" class="dr-mini" data-action="format-down" data-index="${index}" ${index === count - 1 ? 'disabled' : ''} title="아래로">↓</button>
        </td>
        <td class="dr-group-word">묶음 — 안 자리가 하나라도 있으면 감싼다</td>
        <td><input type="text" class="pd-input pd-input-short" data-action="group-prefix" data-index="${index}" value="${escapeHtml(group.prefix)}"></td>
        <td><input type="text" class="pd-input pd-input-short" data-action="group-suffix" data-index="${index}" value="${escapeHtml(group.suffix)}"></td>
        <td></td>
        <td><button type="button" class="dr-mini" data-action="group-unwrap" data-index="${index}" title="괄호를 없애고 안 자리를 밖으로">풀기</button></td>
      </tr>${inners}
      <tr class="dr-inner-row"><td></td><td colspan="5">
        <button type="button" class="dr-mini" data-action="group-format-add" data-index="${index}">+ 안에 자리 추가</button></td></tr>`;
  }

  /** 자리 한 줄 — 맨 위(끌기 대상 · ⠿ n) · 묶음 안(들여 씀 · n.m · ↑↓ 만). 칸마다 data-index · 묶음 안이면 data-inner.
   *  count = 같은 층의 수(↑↓ 끝 잠금) */
  private renderFormatRow(format: TokenFormat, address: FormatAddress, count: number): string {
    const roles = E_DESC_TOKEN_ROLES.map((role) => `<option value="${role}" ${role === format.role ? 'selected' : ''}>${TOKEN_ROLE_WORD[role]} (${role})</option>`).join('');
    const position = address.inner === null ? address.item : address.inner;
    const where = `data-index="${address.item}"${address.inner === null ? '' : ` data-inner="${address.inner}"`}`;
    const rowClass = address.inner === null ? 'dr-format-row' : 'dr-inner-row';
    const order = address.inner === null
      ? `<span class="dr-handle" title="줄의 빈 곳을 끌어 순서를 바꾼다">⠿</span> ${address.item + 1}`
      : `↳ ${address.item + 1}.${address.inner + 1}`;
    return `
      <tr class="${rowClass}" ${where}>
        <td class="dr-order">${order}
          <button type="button" class="dr-mini" data-action="format-up" ${where} ${position === 0 ? 'disabled' : ''} title="위로">↑</button>
          <button type="button" class="dr-mini" data-action="format-down" ${where} ${position === count - 1 ? 'disabled' : ''} title="아래로">↓</button>
        </td>
        <td><select class="pd-input" data-action="format-role" ${where}>${roles}</select></td>
        <td><input type="text" class="pd-input pd-input-short" data-action="format-prefix" ${where} value="${escapeHtml(format.prefix)}"></td>
        <td><input type="text" class="pd-input pd-input-short" data-action="format-suffix" ${where} value="${escapeHtml(format.suffix)}"></td>
        <td><input type="checkbox" data-action="format-required" ${where} ${format.required ? 'checked' : ''} title="필수 — 비면 제품 등록을 막고, 규칙 저장 땐 약식을 비워 고칠 목록에 모읍니다"></td>
        <td><button type="button" class="dr-mini dr-danger" data-action="format-remove" ${where} title="삭제">✕</button></td>
      </tr>`;
  }

  /** 오른쪽 가운데 — 원문 → 결과. null 결과는 필수 빔 — 저장하면 그 행의 약식이 비워진다(2026-10-26 "필수는 입력 검사") */
  private renderPreview(): string {
    const { preview } = this.data;
    const rows = preview.map((line) => `
      <tr><td class="${line.description === null ? 'text-muted' : 'pl-main'}">${line.description === null ? '(필수 빔 — 약식이 비워집니다)' : escapeHtml(line.description)}</td><td class="dr-raw">${escapeHtml(line.raw)}</td></tr>`).join('');
    return `
      <h3 class="dr-h3">미리보기 <span class="text-muted">이 카테고리 원문 ${PREVIEW_LIMIT}줄 — 저장 전</span></h3>
      <table class="pv-table dr-preview-table">
        <thead><tr><th>결과</th><th>원문</th></tr></thead>
        <tbody>${rows.length === 0 ? '<tr><td colspan="2" class="text-muted">자리를 넣으면 보입니다</td></tr>' : rows}</tbody>
      </table>`;
  }

  /** 오른쪽 아래 — 지난 버전 + [이 버전으로] */
  private renderVersions(): string {
    const { versions, form } = this.data;
    if (form === null || form.rule_id === null) return '';
    const rows = versions.map((v) => `
      <tr>
        <td class="mono">버전 ${v.version}</td>
        <td class="text-muted">${v.changed_at.slice(0, 16).replace('T', ' ')}</td>
        <td>${v.changed_by === null ? '' : escapeHtml(v.changed_by)}</td>
        <td class="dr-raw">${escapeHtml(patternWordOf(v.pattern, v.tail))}</td>
        <td><button type="button" class="dr-mini" data-action="restore" data-version="${v.version}">이 버전으로</button></td>
      </tr>`).join('');
    return `
      <h3 class="dr-h3">버전 <span class="text-muted">되돌리기 = 그 버전을 새 버전으로</span></h3>
      <table class="pv-table dr-version-table"><tbody>${rows}</tbody></table>`;
  }

  /** 오른쪽 맨 아래 — 고칠 목록. 지금 저장된 규칙으로 다시 읽으면 필수 자리가 비는 행(약식이 비워졌거나 다음 저장 때 비워짐). 0 줄이면 절째 안 그린다 */
  private renderMissing(): string {
    const { missing, form } = this.data;
    if (form === null || form.rule_id === null || missing.length === 0) return '';
    const cut = missing.length >= MISSING_ROWS_LIMIT ? ` · 앞 ${MISSING_ROWS_LIMIT}줄` : '';
    const rows = missing.map((row) => `
      <tr>
        <td>${missingTargetCellOf(row)}</td>
        <td class="dr-raw">${escapeHtml(row.raw)}</td>
        <td class="text-danger">${row.missing_roles.map((role) => TOKEN_ROLE_WORD[role]).join(' · ')}</td>
      </tr>`).join('');
    return `
      <h3 class="dr-h3">고칠 목록 <span class="text-muted">필수 자리가 비어 약식이 비워진 행 ${missing.length}${cut}</span></h3>
      <table class="pv-table dr-missing-table">
        <thead><tr><th>행</th><th>원문</th><th>빈 자리</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
  }

  // ─── 핸들러 ───
  handleCategory(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, category: (el as HTMLSelectElement).value };
    void this.loadRules(null);
  }

  handleSelectRule(_e: Event, el: HTMLElement): void {
    const id = el.dataset.id ?? '';
    assert(id !== '', '규칙 버튼은 data-id 를 가진다 — renderRuleList 가 붙였다');
    void this.loadRules(id);
  }

  handleNewRule(): void {
    this.data = { ...this.data, form: emptyFormOf(this.data.category), versions: [], preview: [] };
    this.refreshBody();
  }

  handleName(_e: Event, el: HTMLElement): void {
    this.patchForm({ name: (el as HTMLInputElement).value });
    this.refreshDirty();
  }

  handleTail(_e: Event, el: HTMLElement): void {
    const value = (el as HTMLInputElement).value;
    assert((E_RULE_TAILS as readonly string[]).includes(value), `꼬리 라디오 값이 열거 밖이다: ${value} — renderEditor 가 E_RULE_TAILS 로 그렸다`);
    this.patchForm({ tail: value as ERuleTail });
    this.refreshDirty();
    this.schedulePreview();
  }

  handleFormatRole(_e: Event, el: HTMLElement): void {
    const value = (el as HTMLSelectElement).value;
    assert((E_DESC_TOKEN_ROLES as readonly string[]).includes(value), `역할 셀렉트 값이 열거 밖이다: ${value} — renderFormatRow 가 E_DESC_TOKEN_ROLES 로 그렸다`);
    this.patchFormat(addressOf(el), { role: value as TokenFormat['role'] });
  }

  handleFormatPrefix(_e: Event, el: HTMLElement): void {
    this.patchFormat(addressOf(el), { prefix: (el as HTMLInputElement).value });
  }

  handleFormatSuffix(_e: Event, el: HTMLElement): void {
    this.patchFormat(addressOf(el), { suffix: (el as HTMLInputElement).value });
  }

  handleFormatRequired(_e: Event, el: HTMLElement): void {
    this.patchFormat(addressOf(el), { required: (el as HTMLInputElement).checked });
  }

  handleFormatUp(_e: Event, el: HTMLElement): void {
    this.replacePattern(movedFormatAt(this.form().pattern, addressOf(el), -1));
  }

  handleFormatDown(_e: Event, el: HTMLElement): void {
    this.replacePattern(movedFormatAt(this.form().pattern, addressOf(el), 1));
  }

  handleFormatRemove(_e: Event, el: HTMLElement): void {
    this.replacePattern(removedFormat(this.form().pattern, addressOf(el)));
  }

  handleFormatAdd(): void {
    this.replacePattern([...this.form().pattern, { ...NEW_FORMAT }]);
  }

  handleGroupAdd(): void {
    this.replacePattern(addedGroup(this.form().pattern));
  }

  handleGroupPrefix(_e: Event, el: HTMLElement): void {
    this.patchGroup(indexOf(el), { prefix: (el as HTMLInputElement).value });
  }

  handleGroupSuffix(_e: Event, el: HTMLElement): void {
    this.patchGroup(indexOf(el), { suffix: (el as HTMLInputElement).value });
  }

  handleGroupUnwrap(_e: Event, el: HTMLElement): void {
    this.replacePattern(unwrappedGroup(this.form().pattern, indexOf(el)));
  }

  handleGroupFormatAdd(_e: Event, el: HTMLElement): void {
    this.replacePattern(addedFormatInGroup(this.form().pattern, indexOf(el)));
  }

  /** 새 규칙은 바로 만든다. 있는 규칙은 영향 수 확인창 뒤 고친다 */
  async handleSave(): Promise<void> {
    const form = this.form();
    if (form.name.trim() === '') {
      this.data = { ...this.data, error: '이름을 적어야 합니다' };
      this.refreshBody();
      return;
    }
    const ruleId = form.rule_id;
    if (ruleId !== null && !(await this.confirmWithUsage('update', ruleId, inputOfForm(form)))) return;
    await this.run(async () => {
      const saved = ruleId === null ? await api.createDescriptionRule(inputOfForm(form)) : await api.updateDescriptionRule(ruleId, inputOfForm(form));
      return saved.id;
    });
  }

  async handleDelete(): Promise<void> {
    const ruleId = this.savedRuleId();
    if (!(await this.confirmWithUsage('delete', ruleId, null))) return;
    await this.run(async () => {
      await api.deleteDescriptionRule(ruleId);
      return null;
    });
  }

  async handleSetDefault(): Promise<void> {
    const ruleId = this.savedRuleId();
    if (!(await this.confirmWithUsage('set_default', ruleId, null))) return;
    await this.run(async () => (await api.setDefaultDescriptionRule(ruleId)).id);
  }

  async handleRestore(_e: Event, el: HTMLElement): Promise<void> {
    const version = Number(el.dataset.version);
    assert(Number.isInteger(version) && version >= 1, '되돌리기 버튼은 data-version(버전 번호)을 가진다 — renderVersions 가 붙였다');
    const ruleId = this.savedRuleId();
    if (!(await this.confirmWithUsage('restore', ruleId, this.versionInputOf(version)))) return;
    await this.run(async () => (await api.restoreDescriptionRuleVersion(ruleId, version)).id);
  }

  /** 지난 버전 하나를 저장 입력 꼴로 — 되돌리기의 필수 빔 세기 재료. 버전은 renderVersions 가 그린 목록에 있다 */
  private versionInputOf(version: number): DescriptionRuleInput {
    const found = this.data.versions.find((item) => item.version === version);
    assert(found !== undefined, `되돌릴 버전은 받아 둔 버전 목록에 있다 — renderVersions 가 그 목록으로 버튼을 그렸다: ${version}`);
    return { ...inputOfForm(this.form()), pattern: found.pattern, tail: found.tail };
  }

  // ─── 폼 손잡이 ───
  private form(): RuleForm {
    const form = this.data.form;
    assert(form !== null, '편집기 액션은 폼이 있을 때만 그려진다 — renderBody 가 form null 이면 편집기를 안 그린다');
    return form;
  }

  private savedRuleId(): string {
    const ruleId = this.form().rule_id;
    assert(ruleId !== null, '지우기 · 기본 · 되돌리기 버튼은 저장된 규칙에만 그려진다 — renderEditor 가 새 규칙엔 안 붙였다');
    return ruleId;
  }

  private patchForm(patch: Partial<RuleForm>): void {
    this.data = { ...this.data, form: { ...this.form(), ...patch } };
  }

  /** 자리 칸 하나 · 묶음 앞말 뒷말 — 치는 동안 표는 다시 안 그린다(포커스 유지), 미리보기만 */
  private patchFormat(address: FormatAddress, patch: Partial<TokenFormat>): void {
    this.patchPatternQuietly(patchedFormat(this.form().pattern, address, patch));
  }

  private patchGroup(index: number, patch: Partial<Pick<TokenGroup, 'prefix' | 'suffix'>>): void {
    this.patchPatternQuietly(patchedGroup(this.form().pattern, index, patch));
  }

  private patchPatternQuietly(pattern: PatternItem[]): void {
    this.patchForm({ pattern });
    this.refreshDirty();
    this.schedulePreview();
  }

  /** 저장 전 표시만 다시 — 입력 칸은 안 건드린다(포커스 유지) */
  private refreshDirty(): void {
    const root = this._rootElement;
    if (root === undefined) return;
    const badge = root.querySelector<HTMLElement>('.dr-dirty');
    if (badge === null) return;
    const saved = this.data.rules.find((rule) => rule.id === this.form().rule_id);
    const bDirty = saved === undefined || !isSameRule(saved, this.form());
    badge.textContent = bDirty ? '저장 전 — 바뀐 것은 서버에 안 갔습니다' : '저장됨';
    badge.classList.toggle('text-muted', !bDirty);
  }

  /** 자리 줄이 늘거나 줄거나 자리를 바꿈 — 표를 다시 그린다 */
  private replacePattern(pattern: PatternItem[]): void {
    this.patchForm({ pattern });
    this.refreshBody();
    this.schedulePreview();
  }
}

// ─── 순수 ───
function emptyFormOf(category: string): RuleForm {
  return { rule_id: null, spec_category: category, name: '', pattern: [], tail: 'append' };
}

function formOfRule(rule: DescriptionRuleView): RuleForm {
  return { rule_id: rule.id, spec_category: rule.spec_category, name: rule.name, pattern: rule.pattern.map((format) => ({ ...format })), tail: rule.tail };
}

function inputOfForm(form: RuleForm): DescriptionRuleInput {
  return { spec_category: form.spec_category, name: form.name.trim(), pattern: form.pattern, tail: form.tail };
}

/** 액션마다 다시 써질 행 — 고치기 · 되돌리기 = 고른 행 + 기본이면 기본 행 · 지우기 = 고른 행 · 기본 교체 = 기본 행(NULL 전부) */
export function rewriteCountOf(usage: DescriptionRuleUsage, action: RuleAction): RewriteCount {
  switch (action) {
    case 'delete':
      return usage.chosen;
    case 'set_default':
      return usage.category_default;
    case 'update':
    case 'restore':
      return usage.is_default ? addCount(usage.chosen, usage.category_default) : usage.chosen;
    default: {
      const never: never = action;
      throw new Error(`갈래 밖의 액션: ${String(never)}`);
    }
  }
}

export function rewriteWordOf(count: RewriteCount): string {
  return `카탈로그 행 ${count.catalog_rows} · 제품 ${count.products} 이 다시 써집니다`;
}

/** 고칠 목록의 행 칸 — 제품은 상세 새 탭 링크, 카탈로그 행은 표 · #id 글(서버 줄에 파트넘버가 없어 드로어 링크는 다음) */
function missingTargetCellOf(row: DescriptionRuleMissingRow): string {
  if (row.target.table === 'hclt_product') return `<a href="/products/${row.target.id}" target="_blank" rel="noopener">제품 #${row.target.id} ↗</a>`;
  return `<span class="chip chip-muted">${escapeHtml(catalogTableWord(row.target.table))}</span> <span class="mono">#${row.target.id}</span>`;
}

/** 확인창 끝 줄 — 필수가 비는 행이 있을 때만. 세지 않았거나(null) 0 이면 '' */
function missingWordOf(missingOrNull: RewriteCount | null): string {
  if (missingOrNull === null || missingOrNull.catalog_rows + missingOrNull.products === 0) return '';
  return `\n\n필수가 비는 행 — 카탈로그 행 ${missingOrNull.catalog_rows} · 제품 ${missingOrNull.products}. 이 행들은 약식 디스크립션이 비워지고 고칠 목록에 모입니다.`;
}

function addCount(a: RewriteCount, b: RewriteCount): RewriteCount {
  return { catalog_rows: a.catalog_rows + b.catalog_rows, products: a.products + b.products };
}

/** 자리 하나를 위(−1) · 아래(+1)로 — 끝이면 그대로 */
export function movedFormat(pattern: PatternItem[], index: number, delta: -1 | 1): PatternItem[] {
  return movedInList(pattern, index, delta);
}

/** 끌던 자리(from)를 놓은 줄(to)의 자리에 — 그 줄은 밀린다. 같은 자리면 그대로 */
export function droppedFormat(pattern: PatternItem[], from: number, to: number): PatternItem[] {
  if (from === to || from < 0 || to < 0 || from >= pattern.length || to >= pattern.length) return pattern;
  const moved = [...pattern];
  const [picked] = moved.splice(from, 1);
  assert(picked !== undefined, `끌던 자리가 pattern 에 없다: from ${from}, 길이 ${pattern.length} — 손잡이의 data-index 가 그린 표와 어긋났다`);
  moved.splice(to, 0, picked);
  return moved;
}

// ─── 묶음 — 순수(contract 2026-10-26 · 몸통은 fill) ───
// 주소가 그린 표와 어긋나면(없는 항목 · 묶음 아닌 항목에 inner) assert 로 멈춘다.

/** 주소의 자리 하나를 patch 로 — 맨 위 자리든 묶음 안 자리든 */
export function patchedFormat(pattern: PatternItem[], address: FormatAddress, patch: Partial<TokenFormat>): PatternItem[] {
  return replacedItem(pattern, address.item, (item) => {
    if (address.inner === null) {
      assert(item.kind === 'token', `맨 위 자리 주소가 묶음을 가리킨다: item ${address.item} — 그린 표와 어긋났다`);
      return { ...item, ...patch };
    }
    const group = groupOf(item, address);
    return { ...group, formats: replacedFormat(group.formats, address.inner, (format) => ({ ...format, ...patch })) };
  });
}

/** 묶음 하나의 앞말 · 뒷말 */
export function patchedGroup(pattern: PatternItem[], index: number, patch: Partial<Pick<TokenGroup, 'prefix' | 'suffix'>>): PatternItem[] {
  return replacedItem(pattern, index, (item) => ({ ...groupOf(item, { item: index, inner: 0 }), ...patch }));
}

/** 자리 하나를 위(−1) · 아래(+1)로 — inner null 이면 맨 위 항목끼리(movedFormat), 아니면 그 묶음 안에서만. 끝이면 그대로 */
export function movedFormatAt(pattern: PatternItem[], address: FormatAddress, delta: -1 | 1): PatternItem[] {
  if (address.inner === null) return movedFormat(pattern, address.item, delta);
  const inner = address.inner;
  return replacedItem(pattern, address.item, (item) => {
    const group = groupOf(item, address);
    return { ...group, formats: movedInList(group.formats, inner, delta) };
  });
}

/** 자리 하나를 지운다 — 묶음 안의 마지막 자리면 묶음째(백엔드가 빈 묶음을 막는다) */
export function removedFormat(pattern: PatternItem[], address: FormatAddress): PatternItem[] {
  const target = pattern[address.item];
  assert(target !== undefined, `지울 항목이 pattern 에 없다: item ${address.item}, 길이 ${pattern.length} — 그린 표와 어긋났다`);
  if (address.inner === null) return pattern.filter((_item, i) => i !== address.item);

  const group = groupOf(target, address);
  const bLastInGroup = group.formats.length === 1;
  if (bLastInGroup) return pattern.filter((_item, i) => i !== address.item);
  const inner = address.inner;
  return replacedItem(pattern, address.item, () => ({ ...group, formats: group.formats.filter((_format, i) => i !== inner) }));
}

/** 묶음을 그 안 자리들로 바꾼다 — 앞말 · 뒷말은 사라진다 */
export function unwrappedGroup(pattern: PatternItem[], index: number): PatternItem[] {
  const target = pattern[index];
  assert(target !== undefined && target.kind === 'group', `풀 묶음이 없다: item ${index} — 그린 표와 어긋났다`);
  return [...pattern.slice(0, index), ...target.formats, ...pattern.slice(index + 1)];
}

/** 끝에 묶음 하나 — 앞말 '(' · 뒷말 ')' · 안에 NEW_FORMAT 하나 */
export function addedGroup(pattern: PatternItem[]): PatternItem[] {
  return [...pattern, { kind: 'group', prefix: NEW_GROUP_PREFIX, suffix: NEW_GROUP_SUFFIX, formats: [{ ...NEW_FORMAT }] }];
}

/** 그 묶음 끝에 NEW_FORMAT 하나 */
export function addedFormatInGroup(pattern: PatternItem[], index: number): PatternItem[] {
  return replacedItem(pattern, index, (item) => {
    const group = groupOf(item, { item: index, inner: 0 });
    return { ...group, formats: [...group.formats, { ...NEW_FORMAT }] };
  });
}

// 안 함수 — 항목 하나 갈아 끼우기 · 묶음 꺼내기 · 목록 안 옮기기
function replacedItem(pattern: PatternItem[], index: number, change: (item: PatternItem) => PatternItem): PatternItem[] {
  assert(pattern[index] !== undefined, `고칠 항목이 pattern 에 없다: item ${index}, 길이 ${pattern.length} — 그린 표와 어긋났다`);
  return pattern.map((item, i) => (i === index ? change(item) : item));
}

function replacedFormat(formats: TokenFormat[], index: number, change: (format: TokenFormat) => TokenFormat): TokenFormat[] {
  assert(formats[index] !== undefined, `묶음 안 자리가 없다: inner ${index}, 길이 ${formats.length} — 그린 표와 어긋났다`);
  return formats.map((format, i) => (i === index ? change(format) : format));
}

function groupOf(item: PatternItem, address: FormatAddress): TokenGroup {
  assert(item.kind === 'group', `묶음 안 주소인데 항목이 자리다: item ${address.item} — 그린 표와 어긋났다`);
  return item;
}

/** 목록 하나 안에서 한 칸 위(−1) · 아래(+1)로 — 끝이면 그대로. 맨 위 항목 · 묶음 안 자리가 같이 쓴다 */
function movedInList<T>(list: T[], index: number, delta: -1 | 1): T[] {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const moved = [...list];
  const [picked] = moved.splice(index, 1);
  assert(picked !== undefined, `옮길 칸이 목록에 없다: index ${index}, 길이 ${list.length} — 버튼의 data-index 가 그린 표와 어긋났다`);
  moved.splice(target, 0, picked);
  return moved;
}

/** 버전 한 줄의 요약 — "[앞]역할[뒤]* …" 꼴. 묶음은 앞말 · 안 자리 · 뒷말 — (cores·Core clock·GHz) 꼴 */
function patternWordOf(pattern: PatternItem[], tail: ERuleTail): string {
  const parts = pattern.map((item) => (item.kind === 'group' ? `${item.prefix}${item.formats.map(formatWordOf).join(' ')}${item.suffix}` : formatWordOf(item)));
  return `${parts.join(' ')} · ${RULE_TAIL_WORD[tail]}`;
}

function formatWordOf(format: TokenFormat): string {
  return `${format.prefix}${format.role}${format.suffix}${format.required ? '*' : ''}`;
}

/** 입력 · 셀렉트 · 버튼 · 라벨 안을 잡았나 — 거기선 끌지 않는다(글 고르기 · 누르기가 먼저) */
function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('input, select, button, label, textarea') !== null;
}

/** 저장된 규칙과 폼이 같나 — 이름 · 자리 · 꼬리. 같으면 "저장됨" */
function isSameRule(saved: DescriptionRuleView, form: RuleForm): boolean {
  const input = inputOfForm(form);
  return saved.name === input.name && saved.tail === input.tail && JSON.stringify(saved.pattern) === JSON.stringify(input.pattern);
}

function addressOf(el: HTMLElement): FormatAddress {
  const inner = el.dataset.inner;
  return { item: indexOf(el), inner: inner === undefined ? null : innerOf(inner) };
}

function innerOf(text: string): number {
  const inner = Number(text);
  assert(Number.isInteger(inner) && inner >= 0, `묶음 안 자리 칸의 data-inner 가 번호가 아니다: ${text} — renderFormatRow 가 붙였다`);
  return inner;
}

function indexOf(el: HTMLElement): number {
  const index = Number(el.dataset.index);
  assert(Number.isInteger(index) && index >= 0, '자리 줄의 칸은 data-index 를 가진다 — renderFormatRow 가 붙였다');
  return index;
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
